import { lazy } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from './AppShell';
import { SettingsLayout } from './SettingsLayout';
import { buildNav } from './nav';
import { useWhoami } from '../api/whoami';
import { RequireAuth } from '../auth/RequireAuth';
import { RequireRole } from '../auth/RequireRole';
import { RequireModule } from '../auth/RequireModule';
import { WhoamiBoundary } from '../auth/WhoamiBoundary';
import { SignInPage } from '../pages/SignInPage';
import { ForbiddenPage } from '../pages/ForbiddenPage';
import { NotFoundPage } from '../pages/NotFoundPage';
import { OopsPage } from '../pages/OopsPage';
import { SiteAdminShell } from '../siteadmin/app/SiteAdminShell';
import { SaStubPage } from '../siteadmin/app/SaStubPage';
import { SA_ROUTES } from '../siteadmin/app/saNav';

// Slice 16 — Site Admin console home (the console shell + section stubs are eager).
const SaHomePage = lazy(() => import('../siteadmin/home/pages/SaHomePage'));

// Slice 17 — Site Admin customers (CRUD + config + impersonation).
const CustomersListPage = lazy(() => import('../siteadmin/customers/pages/CustomersListPage'));
const NewCustomerPage = lazy(() => import('../siteadmin/customers/pages/NewCustomerPage'));
const CustomerDetailPage = lazy(() => import('../siteadmin/customers/pages/CustomerDetailPage'));

// Slice 09 — Dashboard (INS, Insights-gated; Admin/Manager/User variants).
const DashboardPage = lazy(() => import('../dashboard/pages/DashboardPage'));

// Slice 18 — Site Admin master catalog (carriers / devices / standard plans / features).
const SaCarriersPage = lazy(() => import('../siteadmin/catalog/pages/SaCarriersPage'));
const SaDevicesPage = lazy(() => import('../siteadmin/catalog/pages/SaDevicesPage'));
const SaPlansPage = lazy(() => import('../siteadmin/catalog/pages/SaPlansPage'));
const SaFeaturesPage = lazy(() => import('../siteadmin/catalog/pages/SaFeaturesPage'));

// Slice 19 — Site Admin ingestion monitoring (cross-customer).
const SaRecentFilesPage = lazy(() => import('../siteadmin/ingestion/pages/SaRecentFilesPage'));
const SaImportErrorsPage = lazy(() => import('../siteadmin/ingestion/pages/SaImportErrorsPage'));
const SaFeedStatusPage = lazy(() => import('../siteadmin/ingestion/pages/SaFeedStatusPage'));
const SaWarningsPage = lazy(() => import('../siteadmin/ingestion/pages/SaWarningsPage'));
const SaFeedBindingEditorPage = lazy(
  () => import('../siteadmin/ingestion/pages/SaFeedBindingEditorPage'),
);
const SaUnmatchedLinesPage = lazy(
  () => import('../siteadmin/ingestion/pages/SaUnmatchedLinesPage'),
);
const SaUnmatchedDevicesPage = lazy(
  () => import('../siteadmin/ingestion/pages/SaUnmatchedDevicesPage'),
);

// Slice 21 — Site Admin audit, usage health, jobs (Jobs is an external nav link).
const SaUsageHealthPage = lazy(() => import('../siteadmin/audit/pages/SaUsageHealthPage'));
const SaAuditLogPage = lazy(() => import('../siteadmin/audit/pages/SaAuditLogPage'));
const SaImpersonationLogPage = lazy(
  () => import('../siteadmin/audit/pages/SaImpersonationLogPage'),
);

// Slice 20 — Site Admin identity tools + notifications (cross-customer).
const SaEntraPage = lazy(() => import('../siteadmin/identity/pages/SaEntraPage'));
const SaMergePage = lazy(() => import('../siteadmin/identity/pages/SaMergePage'));
const SaAccessPage = lazy(() => import('../siteadmin/identity/pages/SaAccessPage'));
const SaTemplatesPage = lazy(() => import('../siteadmin/notifications/pages/SaTemplatesPage'));
const SaDeliveryPage = lazy(() => import('../siteadmin/notifications/pages/SaDeliveryPage'));

/** Leaf ids that now render real screens (excluded from the SaStubPage fall-through). */
const SA_REAL_ROUTE_IDS = new Set([
  'home',
  'customers',
  'catalog-carriers',
  'catalog-devices',
  'catalog-plans',
  'catalog-features',
  'ing-recent',
  'ing-errors',
  'ing-feeds',
  'ing-warnings',
  'ing-unmatched-lines',
  'ing-unmatched-devices',
  'usage',
  'audit-log',
  'audit-imp',
  'id-entra',
  'id-merge',
  'id-access',
  'notif-templates',
  'notif-delivery',
]);

// Slice 10 — Reports (INS, Insights-gated; Admin/Manager variants).
const ReportsPage = lazy(() => import('../reports/pages/ReportsPage'));

// Slice 11 — Lines (INV⋈INS; Admin list/detail + User My Lines).
const LinesListPage = lazy(() => import('../lines/pages/LinesListPage'));
const LineDetailPage = lazy(() => import('../lines/pages/LineDetailPage'));
const MyLinesPage = lazy(() => import('../lines/pages/MyLinesPage'));

// Slice 15 — Invoices (INS, Administrator-scoped; list + detail + line-charge detail).
const InvoicesListPage = lazy(() => import('../invoices/pages/InvoicesListPage'));
const InvoiceDetailPage = lazy(() => import('../invoices/pages/InvoiceDetailPage'));
const InvoiceLineChargeDetailPage = lazy(
  () => import('../invoices/pages/InvoiceLineChargeDetailPage'),
);

// Slice 01 — Users CRUD (IDN, Administrator-scoped).
const UsersListPage = lazy(() => import('../users/pages/UsersListPage'));
const UserDetailPage = lazy(() => import('../users/pages/UserDetailPage'));

// Slice 03 — Team & profile (IDN, any authenticated user).
const ProfilePage = lazy(() => import('../profile/pages/ProfilePage'));
const TeamPage = lazy(() => import('../team/pages/TeamPage'));
const TeamMemberPage = lazy(() => import('../team/pages/TeamMemberPage'));

// Slice 04 — Catalog core (CAT, Administrator-scoped, under Settings › Catalog).
const CarriersPage = lazy(() => import('../catalog/pages/CarriersPage'));
const DevicesPage = lazy(() => import('../catalog/pages/DevicesPage'));
const PlansPage = lazy(() => import('../catalog/pages/PlansPage'));
const FeaturesPage = lazy(() => import('../catalog/pages/FeaturesPage'));

// Slice 05 — Catalog packages (CAT, Administrator-scoped, under Settings › Catalog).
const PackagesPage = lazy(() => import('../catalog/pages/PackagesPage'));
const AssignmentRulesPage = lazy(() => import('../catalog/pages/AssignmentRulesPage'));

// Slice 06 — Budgets (CAT, Administrator-scoped, under Settings › Budgets).
const BudgetProfilesPage = lazy(() => import('../budgets/pages/BudgetProfilesPage'));
const BudgetAssignmentRulesPage = lazy(() => import('../budgets/pages/BudgetAssignmentRulesPage'));
const BudgetOverridesPage = lazy(() => import('../budgets/pages/BudgetOverridesPage'));

// Customer-scoped PORTAL branding (IDN, Administrator). Distinct from the
// Notifications › "Branding & sender" screen below, which brands emails.
const BrandingSettingsPage = lazy(() => import('../branding/pages/BrandingSettingsPage'));

// Slice 07 — Notifications (NOTIF, Administrator-scoped, under Settings › Notifications).
const NotificationTemplatesPage = lazy(
  () => import('../notifications/pages/NotificationTemplatesPage'),
);
const NotificationSettingsPage = lazy(
  () => import('../notifications/pages/NotificationSettingsPage'),
);
const NotificationDeliveryPage = lazy(
  () => import('../notifications/pages/NotificationDeliveryPage'),
);

// Slice 08 — Data & ingestion (INV/ING, Administrator-scoped, under Settings › Data).
const CarrierAccountsPage = lazy(() => import('../ingestion/pages/CarrierAccountsPage'));
const ImportWarningsPage = lazy(() => import('../ingestion/pages/ImportWarningsPage'));
const FeedStatusPage = lazy(() => import('../ingestion/pages/FeedStatusPage'));

// Slice 12 — Requests lists + detail (REQ; Admin list / Manager queue / detail).
const RequestsPage = lazy(() => import('../requests/pages/RequestsPage'));
const RequestDetailPage = lazy(() => import('../requests/pages/RequestDetailPage'));

// Slice 13 — Request-submit forms (launcher + device wizard / service change / support).
const RequestLauncherPage = lazy(() => import('../requests/pages/RequestLauncherPage'));
const DeviceAcquisitionForm = lazy(() => import('../requests/pages/DeviceAcquisitionForm'));
const ServiceChangeForm = lazy(() => import('../requests/pages/ServiceChangeForm'));
const TechnicalSupportForm = lazy(() => import('../requests/pages/TechnicalSupportForm'));

// Slice 14 — public magic-link approval landing (anonymous; outside the shell).
const ApprovalLandingPage = lazy(() => import('../approval/pages/ApprovalLandingPage'));

/**
 * Index redirect for `/`. A static redirect to `/dashboard` dead-ended on a
 * masked 404 for any customer with Insights disabled (Dashboard is
 * `RequireModule="insights"`). Instead we resolve the first *enabled* nav
 * destination from `whoami` via `buildNav` and go there, so `/` always lands on
 * a route the effective identity can actually reach.
 *
 * We prefer the first module-backed workspace — Dashboard when Insights is on,
 * otherwise Requests — and fall back to the first nav item for identities whose
 * nav is purely role-scoped. A Settings-style item (with children) resolves to
 * its first child path. While `whoami` loads, a spinner shows in the shell body.
 */
export function IndexRedirect() {
  const { data: whoami, isPending } = useWhoami();

  if (isPending || !whoami) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // A non-impersonating SiteAdministrator has no customer scope — land them in
  // the operator console rather than a customer-portal route that would 403.
  if (whoami.effective.isSiteAdministrator && !whoami.isImpersonating) {
    return <Navigate to="/site" replace />;
  }

  const nav = buildNav(whoami);
  const target = nav.find((item) => item.module) ?? nav[0];
  const destination = target?.children?.[0]?.to ?? target?.to ?? '/dashboard';

  return <Navigate to={destination} replace />;
}

/**
 * The application route tree (react-router v7).
 *
 * Public routes (`/sign-in`, `/forbidden`, `/oops`, `/approval/:action`, and
 * the catch-all 404) render OUTSIDE the shell and the auth guard. Everything
 * else sits under `<RequireAuth><AppShell/></RequireAuth>`, so the chrome and
 * an authenticated session are guaranteed before a feature screen mounts.
 *
 * Since DDR-PLT-024 `RequireAuth` sends an unauthenticated caller straight to
 * the Microsoft sign-in page instead of to `/sign-in`. That makes the public /
 * guarded split load-bearing rather than cosmetic — each of the five public
 * routes has to stay outside the guard for a specific reason:
 *
 *   - `/approval/:action` — the emailed magic-link approve/deny page. Its
 *     approvers are explicitly not required to hold a portal session, so a
 *     bounce to Microsoft would break the whole approval flow (ADR-018).
 *   - `/sign-in` — the registered `postLogoutRedirectUri`, and the exit target
 *     for the sign-in-failed and blocked-account dead ends. It must not
 *     redirect, or sign-out silently re-authenticates via SSO and the
 *     blocked-account card loops.
 *   - `/forbidden`, `/oops` — must render when the shell or the session is the
 *     thing that failed.
 *   - `*` — a typo'd URL from an anonymous visitor should 404, not bounce to
 *     Microsoft.
 *
 * Route guards compose per WEB PRD §2.2:
 *   - Insights-only routes (Dashboard/Lines/Reports/Invoices) → `RequireModule="insights"`.
 *   - Requests → `RequireModule="requests"`.
 *   - Users/Settings → `RequireRole` Administrator.
 * `RequireRole` renders the 403 page in place; `RequireModule` renders 404.
 */
export function AppRoutes() {
  return (
    <Routes>
      {/* Public — outside the shell and the auth guard. */}
      <Route path="/sign-in" element={<SignInPage />} />
      <Route path="/forbidden" element={<ForbiddenPage />} />
      <Route path="/oops" element={<OopsPage />} />

      {/* Public magic-link approval landing (slice 14). Anonymous: the token is
          the only credential, passed in the query string. Renders outside the
          shell / RequireAuth and makes no whoami / MSAL call. `:action` is
          `approve` or `deny`; the page treats any other value as an invalid link. */}
      <Route path="/approval/:action" element={<ApprovalLandingPage />} />

      {/* Authenticated shell layout. `RequireAuth` gates on the MSAL session,
          redirecting an unauthenticated caller straight to Microsoft;
          `WhoamiBoundary` then gates on the whoami load — showing the "signing
          you in" card while it resolves, a blocked dead-end on a 401, and the
          branded shell on success. */}
      <Route
        element={
          <RequireAuth>
            <WhoamiBoundary>
              <AppShell />
            </WhoamiBoundary>
          </RequireAuth>
        }
      >
        <Route index element={<IndexRedirect />} />

        <Route
          path="dashboard"
          element={
            <RequireModule module="insights">
              <DashboardPage />
            </RequireModule>
          }
        />
        {/* Lines inventory: Administrator-scoped. Insights-gated at the route to
            match the foundation nav policy (Lines is hidden when Insights is off);
            the page still degrades to asset-only if moduleSettings.insights is
            false (defensive — the INV asset view itself is not Insights-gated). */}
        <Route
          path="lines"
          element={
            <RequireModule module="insights">
              <RequireRole roles="Administrator">
                <LinesListPage />
              </RequireRole>
            </RequireModule>
          }
        />
        <Route
          path="lines/:id"
          element={
            <RequireModule module="insights">
              <RequireRole roles="Administrator">
                <LineDetailPage />
              </RequireRole>
            </RequireModule>
          }
        />
        {/* Reports is module-gated only (no role guard): `ReportsPage` branches on
            the effective role — Administrator → whole-org (Admin-only endpoints),
            Manager → team-scoped (RequiresManager endpoints, PRD Insights §5.2), and
            a plain User gets an inline "not available" state (no 403, no team call).
            A role guard would wrongly deny the Manager team-reports variant. */}
        <Route
          path="reports"
          element={
            <RequireModule module="insights">
              <ReportsPage />
            </RequireModule>
          }
        />
        {/* Invoices is Administrator-only (PRD Insights §5.3 — no Manager variant;
            it is only in the Administrator nav). Role-gate the routes so a plain
            User or Manager who types the URL gets a clean 403 (slice 15). The
            line-charge detail is rendered from the cached invoice detail (no
            standalone endpoint); the PHONE link in the per-line table routes here
            while phone links elsewhere route to the slice-08 usage line detail. */}
        <Route
          path="invoices"
          element={
            <RequireModule module="insights">
              <RequireRole roles="Administrator">
                <InvoicesListPage />
              </RequireRole>
            </RequireModule>
          }
        />
        <Route
          path="invoices/:invoiceId"
          element={
            <RequireModule module="insights">
              <RequireRole roles="Administrator">
                <InvoiceDetailPage />
              </RequireRole>
            </RequireModule>
          }
        />
        <Route
          path="invoices/:invoiceId/lines/:lineId"
          element={
            <RequireModule module="insights">
              <RequireRole roles="Administrator">
                <InvoiceLineChargeDetailPage />
              </RequireRole>
            </RequireModule>
          }
        />

        <Route
          path="requests"
          element={
            <RequireModule module="requests">
              <RequestsPage />
            </RequireModule>
          }
        />
        {/* New-request launcher + the five submit forms (slice 13). The static
            `new/*` segments win over `requests/:id` (single-segment dynamic). */}
        <Route
          path="requests/new"
          element={
            <RequireModule module="requests">
              <RequestLauncherPage />
            </RequireModule>
          }
        />
        <Route
          path="requests/new/device/:variant"
          element={
            <RequireModule module="requests">
              <DeviceAcquisitionForm />
            </RequireModule>
          }
        />
        <Route
          path="requests/new/service-change"
          element={
            <RequireModule module="requests">
              <ServiceChangeForm />
            </RequireModule>
          }
        />
        <Route
          path="requests/new/support"
          element={
            <RequireModule module="requests">
              <TechnicalSupportForm />
            </RequireModule>
          }
        />
        <Route
          path="requests/:id"
          element={
            <RequireModule module="requests">
              <RequestDetailPage />
            </RequireModule>
          }
        />

        <Route
          path="users"
          element={
            <RequireRole roles="Administrator">
              <UsersListPage />
            </RequireRole>
          }
        />
        <Route
          path="users/:id"
          element={
            <RequireRole roles="Administrator">
              <UserDetailPage />
            </RequireRole>
          }
        />

        {/* My profile — any authenticated user (effective identity). Opened from
            the user menu; no role gate (self-service). */}
        <Route path="profile" element={<ProfilePage />} />

        {/* Manager / User scoped destinations (no module gate). `/team` is
            intentionally not role-guarded: a non-manager gets an empty roster
            (HTTP 200) rendered as an EmptyState, not a 403. */}
        <Route path="team" element={<TeamPage />} />
        {/* The roster's leaf screens ARE role-guarded, unlike `/team` itself: they
            read Manager-gated endpoints, so a plain User typing the URL should get
            a clean 403 rather than a screen that fails its own fetch. Static
            segments (`lines`) outrank the dynamic `members/:userId`, and the two
            sit at different depths besides. Not module-gated: the member profile
            and their line assets are IDN + INV reads, neither Insights-dependent. */}
        <Route
          path="team/members/:userId"
          element={
            <RequireRole roles="Manager">
              <TeamMemberPage />
            </RequireRole>
          }
        />
        {/* Team lines mirrors the Administrator `/lines` pair exactly — same page,
            team scope — including its Insights gate, so the two stay in step. */}
        <Route
          path="team/lines"
          element={
            <RequireModule module="insights">
              <RequireRole roles="Manager">
                <LinesListPage scope="team" />
              </RequireRole>
            </RequireModule>
          }
        />
        <Route
          path="team/lines/:id"
          element={
            <RequireModule module="insights">
              <RequireRole roles="Manager">
                <LineDetailPage scope="team" />
              </RequireRole>
            </RequireModule>
          }
        />
        <Route
          path="my-lines"
          element={
            <RequireModule module="insights">
              <MyLinesPage />
            </RequireModule>
          }
        />

        {/* Settings section (Administrator): the two-pane shell (grouped
            SectionNav + Outlet). Slice 04 lands the Catalog group's
            Carriers/Devices/Plans; every other item routes to StubPage so the
            nav is complete and slices 05–08 just fill their section. */}
        <Route
          path="settings"
          element={
            <RequireRole roles="Administrator">
              <SettingsLayout />
            </RequireRole>
          }
        >
          <Route index element={<Navigate to="/settings/catalog/carriers" replace />} />

          {/* Organization — customer-scoped PORTAL branding. */}
          <Route path="branding" element={<BrandingSettingsPage />} />

          {/* Catalog */}
          <Route path="catalog" element={<Navigate to="/settings/catalog/carriers" replace />} />
          <Route path="catalog/carriers" element={<CarriersPage />} />
          <Route path="catalog/devices" element={<DevicesPage />} />
          <Route path="catalog/plans" element={<PlansPage />} />
          <Route path="catalog/features" element={<FeaturesPage />} />
          <Route path="catalog/packages" element={<PackagesPage />} />
          <Route path="catalog/assignment-rules" element={<AssignmentRulesPage />} />

          {/* Budgets (slice 06) */}
          <Route path="budgets" element={<Navigate to="/settings/budgets/profiles" replace />} />
          <Route path="budgets/profiles" element={<BudgetProfilesPage />} />
          <Route path="budgets/assignment-rules" element={<BudgetAssignmentRulesPage />} />
          <Route path="budgets/overrides" element={<BudgetOverridesPage />} />

          {/* Notifications (slice 07) */}
          <Route
            path="notifications"
            element={<Navigate to="/settings/notifications/templates" replace />}
          />
          <Route path="notifications/templates" element={<NotificationTemplatesPage />} />
          <Route path="notifications/branding" element={<NotificationSettingsPage />} />
          <Route path="notifications/delivery" element={<NotificationDeliveryPage />} />

          {/* Data (slice 08). HR file mapping is SiteAdmin-only (FileSchemaBinding
              authoring) and intentionally absent from the customer Data nav +
              routes — see the goal's consolidation. */}
          <Route path="data" element={<Navigate to="/settings/data/feed-status" replace />} />
          <Route path="data/feed-status" element={<FeedStatusPage />} />
          <Route path="data/accounts" element={<CarrierAccountsPage />} />
          <Route path="data/warnings" element={<ImportWarningsPage />} />
        </Route>
      </Route>

      {/* Site Admin console (`/site`) — a distinct DecisionPoint-internal shell
          gated to SiteAdministrator. `RequireRole` wraps the shell so a
          non-SiteAdmin gets the bare 403 (no console chrome). Home is real
          (slice 16); the other sections are stubs until slices 17–21. */}
      <Route
        path="site"
        element={
          <RequireAuth>
            <WhoamiBoundary>
              <RequireRole roles="SiteAdministrator">
                <SiteAdminShell />
              </RequireRole>
            </WhoamiBoundary>
          </RequireAuth>
        }
      >
        <Route index element={<SaHomePage />} />
        <Route path="customers" element={<CustomersListPage />} />
        <Route path="customers/new" element={<NewCustomerPage />} />
        <Route path="customers/:id" element={<CustomerDetailPage />} />
        {/* Block 094 — feed-binding authoring, nested under the owning customer.
            Without a binding id the editor opens the newest version in the scope
            (or a blank v1); with one it opens exactly that version, which is what
            the per-version Edit links use. */}
        <Route
          path="customers/:customerId/bindings/:feedType"
          element={<SaFeedBindingEditorPage />}
        />
        <Route
          path="customers/:customerId/bindings/:feedType/:bindingId"
          element={<SaFeedBindingEditorPage />}
        />
        {/* Slice 18 — master catalog */}
        <Route path="catalog/carriers" element={<SaCarriersPage />} />
        <Route path="catalog/devices" element={<SaDevicesPage />} />
        <Route path="catalog/plans" element={<SaPlansPage />} />
        <Route path="catalog/features" element={<SaFeaturesPage />} />

        {/* Slice 19 — ingestion monitoring */}
        <Route path="ingestion/recent" element={<SaRecentFilesPage />} />
        <Route path="ingestion/errors" element={<SaImportErrorsPage />} />
        <Route path="ingestion/feeds" element={<SaFeedStatusPage />} />
        <Route path="ingestion/warnings" element={<SaWarningsPage />} />
        <Route path="ingestion/unmatched-lines" element={<SaUnmatchedLinesPage />} />
        <Route path="ingestion/unmatched-devices" element={<SaUnmatchedDevicesPage />} />

        {/* Slice 21 — audit, usage health, impersonation log */}
        <Route path="usage" element={<SaUsageHealthPage />} />
        <Route path="audit/log" element={<SaAuditLogPage />} />
        <Route path="audit/impersonation" element={<SaImpersonationLogPage />} />

        {/* Slice 20 — notifications + identity tools */}
        <Route path="notifications/templates" element={<SaTemplatesPage />} />
        <Route path="notifications/delivery" element={<SaDeliveryPage />} />
        <Route path="identity/entra" element={<SaEntraPage />} />
        <Route path="identity/merge" element={<SaMergePage />} />
        <Route path="identity/access" element={<SaAccessPage />} />

        {SA_ROUTES.filter((leaf) => !SA_REAL_ROUTE_IDS.has(leaf.id)).map((leaf) => (
          <Route
            key={leaf.id}
            path={leaf.to.replace('/site/', '')}
            element={<SaStubPage routeId={leaf.id} />}
          />
        ))}
      </Route>

      {/* Unknown path → public 404. */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
